import { useEffect, useState, type ReactNode } from "react";

import { Dialog } from "@/components/ui/Dialog";
import type { AssetListItem } from "@/lib/api";
import { api } from "@/lib/api";

export type AssetDialogState =
  | null
  | { mode: "rename"; asset: AssetListItem }
  | { mode: "delete"; asset: AssetListItem };

export type AssetActionDone =
  | { kind: "rename"; assetId: string }
  | { kind: "delete"; assetId: string };

interface AssetActionsDialogProps {
  state: AssetDialogState;
  onClose: () => void;
  onDone: (result: AssetActionDone) => void;
}

export function AssetActionsDialog({ state, onClose, onDone }: AssetActionsDialogProps) {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!state) return;
    setLocalError(null);
    setBusy(false);
    if (state.mode === "rename") setInput(state.asset.filename);
    else setInput("");
  }, [state]);

  const open = state !== null;

  const handleRename = async () => {
    if (!state || state.mode !== "rename") return;
    const name = input.trim();
    if (!name) {
      setLocalError("Informe um nome.");
      return;
    }
    if (name === state.asset.filename) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      await api.renameAsset(state.asset.id, name);
      onDone({ kind: "rename", assetId: state.asset.id });
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!state || state.mode !== "delete") return;
    setBusy(true);
    try {
      await api.deleteAsset(state.asset.id);
      onDone({ kind: "delete", assetId: state.asset.id });
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  let title = "";
  let body: ReactNode = null;
  let footer: ReactNode = null;

  if (state?.mode === "rename") {
    title = "Renomear arquivo";
    body = (
      <div className="space-y-3">
        <label className="block text-xs uppercase tracking-wide text-ink-400" htmlFor="asset-rename-input">
          Novo nome
        </label>
        <input
          id="asset-rename-input"
          type="text"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          className="w-full rounded border border-ink-600 bg-ink-950 px-3 py-2 text-ink-100 outline-none focus:border-emerald-600"
          disabled={busy}
        />
      </div>
    );
    footer = (
      <>
        <button
          type="button"
          className="rounded border border-ink-600 px-4 py-2 text-sm text-ink-200 hover:bg-ink-800"
          onClick={onClose}
          disabled={busy}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
          onClick={() => void handleRename()}
          disabled={busy}
        >
          Salvar
        </button>
      </>
    );
  } else if (state?.mode === "delete") {
    title = "Excluir arquivo";
    body = (
      <p className="leading-relaxed">
        Esta ação remove o arquivo e os renders associados.
      </p>
    );
    footer = (
      <>
        <button
          type="button"
          className="rounded border border-ink-600 px-4 py-2 text-sm text-ink-200 hover:bg-ink-800"
          onClick={onClose}
          disabled={busy}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="rounded bg-red-800 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          onClick={() => void handleDelete()}
          disabled={busy}
        >
          Excluir
        </button>
      </>
    );
  }

  return (
    <Dialog title={title} open={open} onClose={busy ? () => {} : onClose} footer={footer}>
      {body}
      {localError ? (
        <p className="mt-3 rounded border border-red-900 bg-red-950/40 px-2 py-1.5 text-xs text-red-300">
          {localError}
        </p>
      ) : null}
    </Dialog>
  );
}
